import { el } from "./dom.js";

export type NavTab = "feed" | "board" | "settings";

export interface NavOptions {
  // null when the current view isn't one of the tabs (e.g. the Work view,
  // reachable by deep link but no longer a top-level destination).
  active: NavTab | null;
  onFeed: () => void;
  onBoard: () => void;
  onSettings: () => void;
}

// Stroked glyphs (Lucide-style, currentColor) so CSS drives the active/inactive
// color. Shapes follow Bottom Nav.svg: rss broadcast, a left-aligned list, and
// a settings gear.
const ICONS: Record<NavTab, string> = {
  feed:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M4 11a9 9 0 0 1 9 9" /><path d="M4 4a16 16 0 0 1 16 16" />' +
    '<circle cx="5" cy="19" r="1" /></svg>',
  board:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="10" x2="13" y2="10" />' +
    '<line x1="3" y1="14" x2="21" y2="14" /><line x1="3" y1="18" x2="13" y2="18" /></svg>',
  settings:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<circle cx="12" cy="12" r="3" />' +
    '<path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>',
};

export function bottomNav(opts: NavOptions): HTMLElement {
  const tabs: { tab: NavTab; label: string; handler: () => void }[] = [
    { tab: "feed",     label: "Feed",     handler: opts.onFeed },
    { tab: "board",    label: "Board",    handler: opts.onBoard },
    { tab: "settings", label: "Settings", handler: opts.onSettings },
  ];

  return el("nav", { class: "bottomnav" }, tabs.map(({ tab, label, handler }) => {
    const isActive = tab === opts.active;
    const attrs: Record<string, string | number | boolean | EventListener> = {
      class: isActive ? "navtab active" : "navtab",
      "data-tab": tab,
      onclick: handler,
    };
    if (isActive) attrs["aria-current"] = "page";

    const button = el("button", attrs) as HTMLButtonElement;
    const icon = el("span", { class: "navicon" });
    icon.innerHTML = ICONS[tab];
    button.append(icon, el("span", { class: "navlabel" }, [label]));
    return button;
  }));
}
